import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import calendarService from '../services/calendarService';

// Respond to calendar share request
export const respondToCalendarShareRequest = createAsyncThunk(
  'calendar/respondToCalendarShareRequest',
  async ({ requestId, action, userId }, { rejectWithValue, dispatch }) => {
    try {
      console.log('[Calendar] Responding to calendar share request:', { requestId, action, userId });
      
      // Use the calendarService
      const response = await calendarService.respondToShareRequest(requestId, userId, action);
      
      // After successful response, refresh the relevant data
      if (response.success !== false) {
        // Refresh share requests
        dispatch(fetchShareRequests(userId));
        
        // If accepted, also refresh shared events
        if (action === 'accept') {
          dispatch(fetchSharedEvents(userId));
        }
      }
      
      return response;
    } catch (error) {
      console.error('[Calendar] Error responding to share request:', error);
      return rejectWithValue({ message: error.message || 'Failed to respond to calendar share request' });
    }
  }
);

// Fetch shared events
export const fetchSharedEvents = createAsyncThunk(
  'calendar/fetchSharedEvents',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      console.log('calendarSlice: Fetching shared events for user:', userId);
      return await calendarService.fetchSharedEvents(userId);
    } catch (error) {
      console.error('calendarSlice: Error fetching shared events:', error);
      return rejectWithValue(error.message || 'Failed to fetch shared events');
    }
  }
);

// Update fetchEvents thunk to use calendarService
export const fetchEvents = createAsyncThunk(
  'calendar/fetchEvents',
  async ({ date, userId }, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      console.log(`calendarSlice: Fetching events for ${date} and user ${userId}`);
      return await calendarService.fetchEvents(date, userId);
    } catch (error) {
      console.error('calendarSlice: Error fetching events:', error);
      return rejectWithValue(error.message || 'Failed to fetch events');
    }
  }
);

// Update fetchWeekEvents thunk to use calendarService
export const fetchWeekEvents = createAsyncThunk(
  'calendar/fetchWeekEvents',
  async ({ date, userId }, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      console.log(`calendarSlice: Fetching week events for ${date} and user ${userId}`);
      return await calendarService.fetchWeekEvents(date, userId);
    } catch (error) {
      console.error('calendarSlice: Error fetching week events:', error);
      return rejectWithValue(error.message || 'Failed to fetch week events');
    }
  }
);

// Update fetchMonthEvents thunk to use calendarService
export const fetchMonthEvents = createAsyncThunk(
  'calendar/fetchMonthEvents',
  async ({ date, userId }, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      console.log(`calendarSlice: Fetching month events for ${date} and user ${userId}`);
      return await calendarService.fetchMonthEvents(date, userId);
    } catch (error) {
      console.error('calendarSlice: Error fetching month events:', error);
      return rejectWithValue(error.message || 'Failed to fetch month events');
    }
  }
);

// Update createEvent thunk to use calendarService
export const createEvent = createAsyncThunk(
  'calendar/createEvent',
  async (eventData, { rejectWithValue, dispatch }) => {
    try {
      if (!eventData || !eventData.userId) {
        return rejectWithValue('Event data and user ID are required');
      }
      console.log('calendarSlice: Creating event:', eventData);
      
      const newEvent = await calendarService.createEvent(eventData);
      
      // Refresh data after creating event
      if (newEvent && newEvent.id) {
        const userId = eventData.userId;
        
        // If event has share requests, refresh those too
        if (eventData.sharedWith && eventData.sharedWith.length > 0) {
          // Allow some time for the backend to process
          setTimeout(() => {
            dispatch(fetchShareRequests(userId));
          }, 1000);
        }
      }
      
      return newEvent;
    } catch (error) {
      console.error('calendarSlice: Error creating event:', error);
      return rejectWithValue(error.message || 'Failed to create event');
    }
  }
);

// Update updateEvent thunk to use calendarService
export const updateEvent = createAsyncThunk(
  'calendar/updateEvent',
  async (eventData, { rejectWithValue, dispatch }) => {
    try {
      if (!eventData || !eventData.id || !eventData.userId) {
        return rejectWithValue('Event data, event ID, and user ID are required');
      }
      console.log('calendarSlice: Updating event:', eventData);
      
      const updatedEvent = await calendarService.updateEvent(eventData);
      
      // Refresh data after updating event
      if (updatedEvent && updatedEvent.id) {
        const userId = eventData.userId;
        
        // If event has share requests, refresh those too
        if (eventData.sharedWith && eventData.sharedWith.length > 0) {
          // Allow some time for the backend to process
          setTimeout(() => {
            dispatch(fetchShareRequests(userId));
          }, 1000);
        }
      }
      
      return updatedEvent;
    } catch (error) {
      console.error('calendarSlice: Error updating event:', error);
      return rejectWithValue(error.message || 'Failed to update event');
    }
  }
);

// Update deleteEvent thunk to use calendarService
export const deleteEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async ({ eventId, userId }, { rejectWithValue }) => {
    try {
      if (!eventId || !userId) {
        return rejectWithValue('Event ID and user ID are required');
      }
      console.log('calendarSlice: Deleting event:', eventId);
      
      // Pass both eventId and userId
      const result = await calendarService.deleteEvent(eventId, userId);
      
      if (result.success) {
        return eventId;
      } else {
        return rejectWithValue('Failed to delete event');
      }
    } catch (error) {
      console.error('calendarSlice: Error deleting event:', error);
      return rejectWithValue(error.message || 'Failed to delete event');
    }
  }
);

// Add a new thunk to fetch share requests
export const fetchShareRequests = createAsyncThunk(
  'calendar/fetchShareRequests',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue('User ID is required');
      }
      console.log('calendarSlice: Fetching share requests for user:', userId);
      return await calendarService.fetchShareRequests(userId);
    } catch (error) {
      console.error('calendarSlice: Error fetching share requests:', error);
      return rejectWithValue(error.message || 'Failed to fetch share requests');
    }
  }
);

// Helper function to sanitize any user ID patterns in names
const sanitizeUserIdInName = (text) => {
  if (typeof text === 'string' && text.match(/^User_[a-zA-Z0-9]+/)) {
    return 'User';
  }
  return text;
};

// Helper function to sanitize share requests
const sanitizeShareRequests = (requests) => {
  if (!Array.isArray(requests)) return [];
  
  return requests.map(request => {
    // Create a shallow copy to avoid mutating the original
    const sanitized = { ...request };
    
    // Check and fix name fields that have User_ID pattern
    if (sanitized.name) {
      sanitized.name = sanitizeUserIdInName(sanitized.name);
    }
    
    if (sanitized.senderName) {
      sanitized.senderName = sanitizeUserIdInName(sanitized.senderName);
    }
    
    // Ensure status is always preserved
    if (!sanitized.status && request.status) {
      sanitized.status = request.status;
    }
    
    return sanitized;
  });
};

export const calendarSlice = createSlice({
  name: 'calendar',
  initialState: {
    sharedEventsNeedRefresh: false,
    loading: false,
    error: null,
    selectedDate: new Date().toString(),
    events: [],
    sharedEvents: [],
    isLoadingShared: false,
    sharedEventsError: null,
    selectedEvent: null,
    shareRequests: [], // Store share requests
    isLoadingShareRequests: false, // Loading state for share requests
    shareRequestsError: null // Error state for share requests
  },
  reducers: {
    // Mark shared events as refreshed
    markSharedEventsRefreshed(state) {
      state.sharedEventsNeedRefresh = false;
    },
    
    // Set the selected date in the calendar
    setSelectedDate(state, action) {
      state.selectedDate = action.payload.selectedDate;
    },
    
    // Set shared events directly (used by components)
    setSharedEvents(state, action) {
      console.log('Setting shared events in Redux state:', action.payload.length);
      state.sharedEvents = action.payload;
      state.sharedEventsError = null;
    },
    
    // Set loading state for shared events
    setLoadingShared(state, action) {
      state.isLoadingShared = action.payload;
    },
    
    // Set shared events error state for shared events
    setSharedEventsError(state, action) {
      state.sharedEventsError = action.payload;
      console.error('Shared events error in Redux:', action.payload);
    },
    
    // Set the selected calendar event
    setCalendarSelectedEvent(state, action) {
      state.selectedEvent = action.payload;
      console.log('Setting selected calendar event:', action.payload?.title);
    },
    
    // Set share requests directly (used by components)
    setShareRequests(state, action) {
      console.log('Setting share requests in Redux state:', action.payload.length);
      state.shareRequests = sanitizeShareRequests(action.payload);
      state.shareRequestsError = null;
    },
    
    // Set loading state for share requests
    setLoadingShareRequests(state, action) {
      state.isLoadingShareRequests = action.payload;
    },
    
    // Set error state for share requests
    setShareRequestsError(state, action) {
      state.shareRequestsError = action.payload;
      console.error('Share requests error in Redux:', action.payload);
    },
    
    // Clear all calendar data (for logout)
    clearCalendarData(state) {
      state.events = [];
      state.sharedEvents = [];
      state.shareRequests = [];
      state.selectedEvent = null;
      state.error = null;
      state.sharedEventsError = null;
      state.shareRequestsError = null;
      
      // Also clear caches in the service
      calendarService.clearCaches();
    },
    setReminder: (state, action) => {
      const { eventId, reminderTime } = action.payload;
      state.events = state.events.map(event => 
        event.id === eventId ? { ...event, reminderTime } : event
      );
    },
    shareEvent: (state, action) => {
      const { eventId, userId } = action.payload;
      // Add logic to handle event sharing
      state.events = state.events.map(event => 
        event.id === eventId ? { ...event, sharedWith: [...(event.sharedWith || []), userId] } : event
      );
    }
  },
  extraReducers: (builder) => {
    // Handle fetchEvents thunk
    builder.addCase(fetchEvents.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEvents.fulfilled, (state, action) => {
      state.loading = false;
      state.events = action.payload.events || [];
    });
    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to fetch events';
    });
    
    // Handle fetchWeekEvents/fetchMonthEvents thunks similarly to fetchEvents
    
    // Handle createEvent thunk
    builder.addCase(createEvent.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createEvent.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        // Add new event to the list if it's not there already
        const eventExists = state.events.some(event => event.id === action.payload.id);
        if (!eventExists) {
          state.events.push(action.payload);
        }
      }
    });
    builder.addCase(createEvent.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to create event';
    });
    
    // Handle updateEvent thunk
    builder.addCase(updateEvent.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateEvent.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        // Update existing event
        const index = state.events.findIndex(event => event.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        } else {
          state.events.push(action.payload);
        }
        
        // Also update in shared events if it exists there
        const sharedIndex = state.sharedEvents.findIndex(event => event.id === action.payload.id);
        if (sharedIndex !== -1) {
          // Preserve the isShared flag
          state.sharedEvents[sharedIndex] = {
            ...action.payload,
            isShared: true,
            shareStatus: 'accepted'
          };
        }
      }
    });
    builder.addCase(updateEvent.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to update event';
    });
    
    // Handle deleteEvent thunk
    builder.addCase(deleteEvent.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteEvent.fulfilled, (state, action) => {
      state.loading = false;
      const eventId = action.payload;
      // Remove from events list
      state.events = state.events.filter(event => event.id !== eventId);
      // Also remove from shared events
      state.sharedEvents = state.sharedEvents.filter(event => event.id !== eventId);
    });
    builder.addCase(deleteEvent.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to delete event';
    });
    
    // Handle respond to calendar share request
    builder.addCase(respondToCalendarShareRequest.pending, (state) => {
      state.loading = true;
      state.error = null;
      console.log('[Calendar Slice] Processing response to calendar share request...');
    });
    builder.addCase(respondToCalendarShareRequest.fulfilled, (state, action) => {
      state.loading = false;
      console.log('[Calendar Slice] Successfully responded to calendar share request:', action.payload);
      
      // If accepted, we need to update the events list to include the shared event
      if (action.payload.action === 'accept') {
        // The shared events will be fetched on the next calendar view refresh
        state.sharedEventsNeedRefresh = true;
        console.log('[Calendar Slice] Marked shared events for refresh');
      }
      
      // Remove the request from the pending requests list
      if (action.payload.requestId) {
        state.shareRequests = state.shareRequests.filter(req => req.id !== action.payload.requestId);
      }
    });
    builder.addCase(respondToCalendarShareRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to respond to calendar share request';
      console.error('[Calendar Slice] Failed to respond to calendar share request:', action.payload);
    });
    
    // Handle fetchSharedEvents thunk
    builder.addCase(fetchSharedEvents.pending, (state) => {
      state.isLoadingShared = true;
      state.sharedEventsError = null;
    });
    builder.addCase(fetchSharedEvents.fulfilled, (state, action) => {
      state.isLoadingShared = false;
      const events = action.payload.events || [];
      console.log('[Calendar Slice] Successfully fetched shared events:', events.length);
      state.sharedEvents = events;
    });
    builder.addCase(fetchSharedEvents.rejected, (state, action) => {
      state.isLoadingShared = false;
      state.sharedEventsError = action.payload || 'Failed to fetch shared events';
      console.error('[Calendar Slice] Failed to fetch shared events:', action.payload);
    });
    
    // Handle fetchShareRequests thunk
    builder.addCase(fetchShareRequests.pending, (state) => {
      state.isLoadingShareRequests = true;
      state.shareRequestsError = null;
    });
    builder.addCase(fetchShareRequests.fulfilled, (state, action) => {
      state.isLoadingShareRequests = false;
      const requests = action.payload.requests || [];
      console.log('[Calendar Slice] Successfully fetched share requests:', requests.length);
      
      // Detailed logging for debugging
      console.log('[Calendar Slice] Raw requests data:', JSON.stringify(requests));
      
      if (requests.length > 0) {
        // Log details of the first request including its status
        const firstRequest = requests[0];
        console.log('[Calendar Slice] First request sample:', {
          id: firstRequest.id,
          status: firstRequest.status,
          hasStatus: 'status' in firstRequest,
          statusType: typeof firstRequest.status
        });
        
        // Log all requests statuses
        console.log('[Calendar Slice] All request statuses:', 
          requests.map(req => ({ id: req.id, status: req.status })));
      }
      
      // Sanitize the incoming requests to fix User_ID patterns
      state.shareRequests = sanitizeShareRequests(requests);
      
      // Log the state after sanitization
      console.log('[Calendar Slice] Sanitized shareRequests length:', state.shareRequests.length);
      if (state.shareRequests.length > 0) {
        console.log('[Calendar Slice] First sanitized request:', {
          id: state.shareRequests[0].id,
          status: state.shareRequests[0].status,
          hasStatus: 'status' in state.shareRequests[0],
        });
      }
    });
    builder.addCase(fetchShareRequests.rejected, (state, action) => {
      state.isLoadingShareRequests = false;
      state.shareRequestsError = action.payload || 'Failed to fetch share requests';
      console.error('[Calendar Slice] Failed to fetch share requests:', action.payload);
    });
  },
});

export const { 
  markSharedEventsRefreshed, 
  setSelectedDate, 
  setSharedEvents, 
  setLoadingShared, 
  setSharedEventsError,
  setCalendarSelectedEvent,
  setShareRequests,
  setLoadingShareRequests,
  setShareRequestsError,
  clearCalendarData,
  setReminder,
  shareEvent
} = calendarSlice.actions;
export default calendarSlice.reducer; 